import React from 'react'
// @src imports
import { useInfiniteGifts } from 'src/react_query'
import { GiftFragment } from 'src/graphql/generated/graphql'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { Icon } from 'src/chrome'

import GiftTiles from '../GiftTiles/GiftTiles'
import MoreIcon from '../MoreIcon/MoreIcon'
import NotFound from '../NotFound/NotFound'
import styles from './giftRibbon.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'
import { GiftTileLoader } from '../GiftTile/GiftTileLoader'

interface Props {
  id?: number
  name: string
  categoryId?: string
  shouldWrapContent?: boolean
  viewGift: (slug: string) => void
  addGift: (gift: GiftFragment) => void
  search?: string
  onSelect?: () => void
  onBack?: () => void
  isChangingGift: boolean
  isEligibleFreeGift?: boolean
}

const GiftRibbon: React.FC<Props> = props => {
  const {
    onSelect,
    id,
    addGift,
    categoryId,
    name,
    shouldWrapContent,
    viewGift,
    search,
    onBack,
    isChangingGift,
    isEligibleFreeGift,
  } = props

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteGifts({
      category: categoryId,
      search,
      isEligibleFreeGift,
    })

  // Flatten paginated data - types are now compatible
  const gifts = data?.pages.flatMap(page => page) ?? []

  // Map to legacy interface for minimal component changes
  const isLoadingMore = isFetchingNextPage
  const hasMore = hasNextPage ?? false
  const loadMore = () => fetchNextPage()

  return (
    <div
      className={`${styles.GiftRibbon} ${
        shouldWrapContent && styles.gridPadding
      }`}
    >
      {shouldWrapContent && onBack && (
        <div className={styles.closeCollection} onClick={() => onBack()}>
          <Icon icon={'CARROTLEFT'} size={12} color={''} />
          <Text type="largeBody" color="primaryHeading">
            BACK
          </Text>
        </div>
      )}
      <div className={styles.ribbonInfo}>
        <div onClick={() => onSelect?.()} style={{ cursor: 'pointer' }}>
          <Text type="largeBody" color="primaryHeading">
            {name}
          </Text>
        </div>
        {!shouldWrapContent && hasMore && <MoreIcon onClick={onSelect} />}
      </div>
      {!isLoading && gifts.length === 0 && <NotFound />}
      <div
        className={styles.ribbonCards}
        style={
          shouldWrapContent
            ? { flexWrap: 'wrap', justifyContent: 'center' }
            : {}
        }
      >
        {isLoading ? (
          <>
            {/* Show skeleton loaders on initial load */}
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <GiftTileLoader key={`initial-loader-${i}`} />
            ))}
          </>
        ) : (
          <GiftTiles
            isChangingGift={isChangingGift}
            id={id}
            viewGift={viewGift}
            addGift={addGift}
            gifts={gifts}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            loadMore={loadMore}
          />
        )}
      </div>
    </div>
  )
}

const Unresolved = () => (
  <div style={{ background: 'transparent', boxShadow: 'none' }}>
    {/* Empty unresolved state - we handle loading with skeleton loaders now */}
  </div>
)

export default suspenseBoundary({
  component: GiftRibbon,
  failure: error => `Failed loading gifts: ${error}`,
  unresolved: <Unresolved />,
})
